/**
 * GROUPED ROW GRIDS — the pure half (#2375, ADR F-84 §2).
 *
 * A grid grouped by one field shows each group's header with the group's
 * count in the WHOLE result — after authorization, search and filtering — and
 * pages flatly through the rows, repeating a group's header when it crosses a
 * page boundary. The failure this exists to prevent is the one #113 measured
 * for filters: grouping the rows that happen to be on this page and letting
 * the headers read as if they were the whole list.
 *
 * What makes an exact answer possible is the platform's own definition of a
 * groupable field: an ENUMERABLE one (`core.assert_view_kind_shape` — field
 * kind option/boolean, declared options, a status machine). Its values are
 * known before any row is read, so:
 *
 *   · group ORDER is the field's declared order (option or status order; Yes
 *     before No), then "Other values" (a stored value outside the declared
 *     set), then "Not provided" (no value at all). A descending sort on the
 *     grouping field reverses the declared part; Other and Not provided stay
 *     last either way;
 *   · group TOTALS are one count per declared value (+ Other + Not provided),
 *     each a bounded server count under the caller's RLS;
 *   · a PAGE is planned from those totals: the flat range [offset, offset +
 *     limit) maps onto per-group slices, each read with its own offset.
 *
 * Nothing here reads data or knows a transport. The host supplies counts and
 * rows; this answers what to ask for and how to draw it.
 */

/** A group key that is not a declared value. */
export const GROUP_OTHER = '__other__'
export const GROUP_MISSING = '__missing__'
export const GROUP_OTHER_LABEL = 'Other values'
export const GROUP_MISSING_LABEL = 'Not provided'

export interface GroupDomainValue {
  /** Stable key — `v:<value>`. */
  key: string
  /** The stored value, as the column holds it. */
  value: string | boolean
  /** The field's normal display label for it. */
  label: string
}

export interface GroupDomain {
  column: string
  /** Declared values, in the field's own order. */
  values: readonly GroupDomainValue[]
  /** A text column: `''` is missing too. */
  textual: boolean
}

export const groupValueKey = (value: string | boolean): string => `v:${String(value)}`

/** A domain from an option list (option values in declared order). */
export function optionGroupDomain(
  column: string,
  options: readonly { value: string; label: string }[],
): GroupDomain {
  return {
    column,
    textual: true,
    values: options.map((option) => ({ key: groupValueKey(option.value), value: option.value, label: option.label })),
  }
}

/** A boolean field's domain: Yes, then No. */
export function booleanGroupDomain(column: string): GroupDomain {
  return {
    column,
    textual: false,
    values: [
      { key: groupValueKey(true), value: true, label: 'Yes' },
      { key: groupValueKey(false), value: false, label: 'No' },
    ],
  }
}

/** Which group a stored value belongs to. */
export function groupKeyOf(domain: GroupDomain, raw: unknown): string {
  if (raw == null || (domain.textual && raw === '')) return GROUP_MISSING
  const text = typeof raw === 'boolean' ? String(raw) : String(raw)
  const hit = domain.values.find((value) => String(value.value) === text)
  return hit ? hit.key : GROUP_OTHER
}

/** The group's label in the field's display formatting. */
export function groupLabelOf(domain: GroupDomain, key: string): string {
  if (key === GROUP_MISSING) return GROUP_MISSING_LABEL
  if (key === GROUP_OTHER) return GROUP_OTHER_LABEL
  return domain.values.find((value) => value.key === key)?.label ?? key
}

/** Group order: declared (reversed when descending), then Other, then Not provided. */
export function orderedGroupKeys(domain: GroupDomain, direction: 'asc' | 'desc' = 'asc'): string[] {
  const declared = domain.values.map((value) => value.key)
  if (direction === 'desc') declared.reverse()
  return [...declared, GROUP_OTHER, GROUP_MISSING]
}

export interface GroupTotal { key: string; total: number }
export interface GroupSlice { key: string; offset: number; limit: number }
export interface GroupedPagePlan {
  /** What to read, in drawing order. Empty groups are skipped. */
  slices: GroupSlice[]
  /** The whole result's size — the sum of the group totals. */
  total: number
  /** Groups whose rows began on an earlier page. */
  continued: Set<string>
}

/**
 * Map a FLAT page onto per-group slices. `groups` is in drawing order with
 * each group's whole-result total. Pure, and the reason grouped pages can be
 * both flat and globally correct: the page is a window over the concatenation
 * of the groups, never a re-grouping of whatever a flat read returned.
 */
export function planGroupedPage(groups: readonly GroupTotal[], offset: number, limit: number): GroupedPagePlan {
  const start = Math.max(0, offset)
  const end = start + Math.max(0, limit)
  const slices: GroupSlice[] = []
  const continued = new Set<string>()
  let cursor = 0
  let total = 0
  for (const group of groups) {
    const size = Math.max(0, group.total)
    total += size
    const from = cursor
    const to = cursor + size
    cursor = to
    if (size === 0) continue
    const lo = Math.max(from, start)
    const hi = Math.min(to, end)
    if (hi <= lo) continue
    slices.push({ key: group.key, offset: lo - from, limit: hi - lo })
    if (lo > from) continued.add(group.key)
  }
  return { slices, total, continued }
}

/**
 * Group a COMPLETE, client-resident result in memory: rows reordered into the
 * group order (stable within each group — the host's own sort is kept), with
 * each group's total. Only for a result the host KNOWS is complete; a capped
 * page grouped here would be exactly the defect this module prevents.
 */
export function groupRowsInMemory<R>(
  rows: readonly R[],
  domain: GroupDomain,
  valueOf: (row: R) => unknown,
  direction: 'asc' | 'desc' = 'asc',
): { rows: R[]; totals: Map<string, number>; keyOf: (row: R) => string } {
  const order = orderedGroupKeys(domain, direction)
  const rank = new Map(order.map((key, index) => [key, index]))
  const keys = new Map<R, string>()
  const totals = new Map<string, number>()
  for (const row of rows) {
    const key = groupKeyOf(domain, valueOf(row))
    keys.set(row, key)
    totals.set(key, (totals.get(key) ?? 0) + 1)
  }
  const ordered = rows
    .map((row, index) => ({ row, index, rank: rank.get(keys.get(row)!) ?? order.length }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ row }) => row)
  return { rows: ordered, totals, keyOf: (row) => keys.get(row) ?? groupKeyOf(domain, valueOf(row)) }
}
